import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  FlatList,
  TextInput,
  Dimensions,
} from 'react-native';
import { InsetModal as Modal } from '../components/InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, FormHeader, ConfirmationModal, Input, Button } from '../components';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface QuickReplySetItemsScreenProps {
  setId?: string;
}

export const QuickReplySetItemsScreen: React.FC<QuickReplySetItemsScreenProps> = ({ setId }) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, activeTab, setActiveTab, screenParams } = useAppContext();
  const [loading, setLoading] = useState(true);
  const [quickReplySet, setQuickReplySet] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [listButtonText, setListButtonText] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [availableRepliesSets, setAvailableRepliesSets] = useState<Array<{ _id: string; name: string }>>([]);
  const [showPickItemsModal, setShowPickItemsModal] = useState(false);
  const [availableItems, setAvailableItems] = useState<any[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [itemsSearchQuery, setItemsSearchQuery] = useState('');
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);

  const actualSetId = setId || (screenParams as any)?.setId;

  useEffect(() => {
    if (actualSetId) {
      loadQuickReplySet();
      loadAvailableRepliesSets();
    }
  }, [actualSetId]);

  const loadQuickReplySet = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getQuickReplySetById(actualSetId!);

      if (response.statusCode === 1000 && response.data) {
        setQuickReplySet(response.data);
        setListButtonText(response.data.listButtonText || '');
        // Sort items by displayOrder to ensure correct ordering
        const sortedItems = (response.data.items || []).sort((a: any, b: any) => {
          const orderA = a.displayOrder ?? 0;
          const orderB = b.displayOrder ?? 0;
          return orderA - orderB;
        });
        
        // Debug: Log items and their IDs
        console.log('📦 [QuickReplySetItemsScreen] Loaded items:', {
          itemCount: sortedItems.length,
          items: sortedItems.map((item: any, idx: number) => ({
            index: idx,
            _id: item._id,
            _idType: typeof item._id,
            displayText: item.displayText,
            displayOrder: item.displayOrder
          }))
        });
        
        setItems(sortedItems);
      } else {
        const errorMessage = response.errors?.[0]?.message || 'Failed to load quick reply set';
        showNotification('error', 'Error', errorMessage);
        navigateTo('quick-replies');
      }
    } catch (error: any) {
      console.error('Error loading quick reply set:', error);
      showNotification('error', 'Error', error.message || 'Failed to load quick reply set');
      navigateTo('quick-replies');
    } finally {
      setLoading(false);
    }
  }, [actualSetId, navigateTo, showNotification]);

  const loadAvailableRepliesSets = async () => {
    try {
      const response = await apiService.getQuickReplySets({});
      if (response.statusCode === 1000 && response.data?.quickReplySets) {
        // Filter out the current set to prevent self-reference
        const filtered = response.data.quickReplySets
          .filter((set: any) => set._id !== actualSetId)
          .map((set: any) => ({ _id: set._id, name: set.name }));
        setAvailableRepliesSets(filtered);
      }
    } catch (error) {
      console.error('Error loading replies sets:', error);
    }
  };

  const loadAvailableItems = async () => {
    try {
      setLoadingItems(true);
      const response = await apiService.getQuickReplyItems({
        limit: 1000,
        skip: 0,
      });
      if (response.statusCode === 1000 && response.data?.items) {
        // Filter out items that are already in this set
        const currentItemIds = items.map((item: any) => item._id).filter(Boolean);
        const filtered = response.data.items
          .filter((item: any) => !currentItemIds.includes(item._id))
          .map((item: any) => item);
        setAvailableItems(filtered);
      }
    } catch (error) {
      console.error('Error loading available items:', error);
      showNotification('error', 'Error', 'Failed to load available items');
    } finally {
      setLoadingItems(false);
    }
  };

  const handleAddExistingItems = async () => {
    if (selectedItemIds.length === 0) {
      showNotification('error', 'Error', 'Please select at least one item');
      return;
    }

    // Remove duplicates from selectedItemIds
    const uniqueItemIds = Array.from(new Set(selectedItemIds));
    if (uniqueItemIds.length !== selectedItemIds.length) {
      showNotification('warning', 'Warning', 'Removed duplicate selections');
    }

    // Filter out items that are already in the set
    const currentItemIds = items.map((item: any) => item._id).filter(Boolean);
    const itemsToAdd = uniqueItemIds.filter(itemId => !currentItemIds.includes(itemId));

    if (itemsToAdd.length === 0) {
      showNotification('error', 'Error', 'All selected items are already in this set');
      return;
    }

    if (itemsToAdd.length !== uniqueItemIds.length) {
      showNotification('warning', 'Warning', `${uniqueItemIds.length - itemsToAdd.length} item(s) were already in the set`);
    }

    try {
      setSaving(true);
      
      // Calculate displayOrder for all items upfront based on current items count
      // This ensures proper alignment when adding multiple items
      const baseDisplayOrder = items.length;
      
      // Add each selected item to the set by passing itemId
      for (let i = 0; i < itemsToAdd.length; i++) {
        const itemId = itemsToAdd[i];
        // Find the full item data from availableItems
        const item = availableItems.find((item: any) => item._id === itemId);
        if (!item) {
          showNotification('error', 'Error', `Item ${itemId} not found`);
          continue;
        }

        // Pass itemId to link existing item instead of creating new one
        // Calculate displayOrder based on base + index to ensure sequential ordering
        const itemData = {
          itemId: itemId, // This tells the backend to use existing item
          displayOrder: baseDisplayOrder + i, // Sequential ordering starting from current items count
        };

        const response = await apiService.addItemToQuickReplySet(actualSetId!, itemData);
        if (response.statusCode !== 1000) {
          const errorMessage = response.errors?.[0]?.message || 'Failed to add item';
          showNotification('error', 'Error', errorMessage);
          return;
        }
      }

      showNotification('success', 'Success', `${itemsToAdd.length} item(s) added successfully.`);
      setShowPickItemsModal(false);
      setSelectedItemIds([]);
      setItemsSearchQuery('');
      // Reload the set to get the updated items with their new IDs
      await loadQuickReplySet();
    } catch (error: any) {
      console.error('Error adding items:', error);
      showNotification('error', 'Error', error.message || 'Failed to add items');
    } finally {
      setSaving(false);
    }
  };

  const handleItemToggle = (itemId: string) => {
    // Check if item is already in the set
    const currentItemIds = items.map((item: any) => item._id).filter(Boolean);
    if (currentItemIds.includes(itemId)) {
      showNotification('warning', 'Warning', 'This item is already in the set');
      return;
    }

    // Toggle selection (prevent duplicates)
    setSelectedItemIds((prev) => {
      if (prev.includes(itemId)) {
        return prev.filter((id) => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  const handleDeleteItem = (itemId: string) => {
    setDeletingItemId(itemId);
    setShowDeleteModal(true);
  };

  const confirmDeleteItem = async () => {
    if (!deletingItemId) return;

    try {
      setDeleting(true);

      const response = await apiService.removeItemFromQuickReplySet(actualSetId!, deletingItemId);

      if (response.statusCode === 1000) {
        showNotification('success', 'Success', 'Item deleted successfully.');
        setShowDeleteModal(false);
        setDeletingItemId(null);
        await loadQuickReplySet();
      } else {
        const errorMessage = response.errors?.[0]?.message || 'Failed to delete item';
        showNotification('error', 'Error', errorMessage);
      }
    } catch (error: any) {
      console.error('Error deleting item:', error);
      showNotification('error', 'Error', error.message || 'Failed to delete item');
    } finally {
      setDeleting(false);
    }
  };

  const handleMoveItem = async (itemId: string, direction: 'up' | 'down') => {
    console.log('🔄 [QuickReplySetItemsScreen] handleMoveItem called:', { itemId, direction, currentItemsCount: items.length });
    
    // Validate we have items to reorder
    if (!items || items.length === 0) {
      console.error('❌ [QuickReplySetItemsScreen] Cannot reorder: No items in set');
      showNotification('error', 'Error', 'No items found in this set. Please add items first.');
      await loadQuickReplySet(); // Reload to sync with server
      return;
    }
    
    // First, ensure we have the latest data by finding the item
    const currentIndex = items.findIndex((item) => {
      const itemIdStr = item._id ? String(item._id) : null;
      const searchIdStr = String(itemId);
      return itemIdStr === searchIdStr;
    });
    
    if (currentIndex === -1) {
      console.error('❌ [QuickReplySetItemsScreen] Item not found for reordering:', {
        itemId,
        searchIdType: typeof itemId,
        availableItems: items.map((item, idx) => ({
          index: idx,
          _id: item._id,
          _idType: typeof item._id,
          _idString: item._id ? String(item._id) : 'MISSING',
          displayText: item.displayText
        }))
      });
      showNotification('error', 'Error', 'Item not found. Please refresh the page and try again.');
      // Reload to get fresh data
      await loadQuickReplySet();
      return;
    }

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= items.length) {
      console.warn('⚠️ [QuickReplySetItemsScreen] Cannot move item: already at boundary');
      return;
    }

    // Store original items for potential revert
    const originalItems = [...items];

    // Optimistically update the UI immediately
    const newItems = [...items];
    [newItems[currentIndex], newItems[newIndex]] = [newItems[newIndex], newItems[currentIndex]];
    
    // Update displayOrder to match new positions
    newItems.forEach((item, idx) => {
      item.displayOrder = idx;
    });
    
    setItems(newItems);

    // Extract IDs in new order and ensure they are strings
    // Filter out any items without IDs (shouldn't happen, but safety check)
    const itemIds = newItems
      .map((item, idx) => {
        if (!item._id) {
          console.error(`❌ [QuickReplySetItemsScreen] Item at index ${idx} missing _id:`, item);
          return null;
        }
        const idStr = String(item._id);
        if (!idStr || idStr === 'undefined' || idStr === 'null') {
          console.error(`❌ [QuickReplySetItemsScreen] Item at index ${idx} has invalid _id:`, { _id: item._id, idStr });
          return null;
        }
        return idStr;
      })
      .filter((id): id is string => id !== null && id !== '');

    // Validate we have all item IDs
    if (itemIds.length !== newItems.length) {
      console.error('❌ [QuickReplySetItemsScreen] Some items are missing IDs. Cannot reorder.', {
        expectedCount: newItems.length,
        actualCount: itemIds.length,
        items: newItems.map((item, idx) => ({ 
          index: idx, 
          _id: item._id, 
          _idType: typeof item._id,
          _idString: item._id ? String(item._id) : 'MISSING',
          displayText: item.displayText 
        }))
      });
      setItems(originalItems);
      showNotification('error', 'Error', 'Some items are missing IDs. Please refresh and try again.');
      return;
    }

    // Validate setId before making API call
    if (!actualSetId) {
      console.error('❌ [QuickReplySetItemsScreen] Cannot reorder: setId is missing');
      setItems(originalItems);
      showNotification('error', 'Error', 'Set ID is missing. Please refresh the page and try again.');
      return;
    }

    // Validate setId format (should be 24 character hex string)
    if (typeof actualSetId !== 'string' || actualSetId.length !== 24 || !/^[0-9a-fA-F]{24}$/.test(actualSetId)) {
      console.error('❌ [QuickReplySetItemsScreen] Invalid setId format:', {
        setId: actualSetId,
        type: typeof actualSetId,
        length: actualSetId?.length
      });
      setItems(originalItems);
      showNotification('error', 'Error', 'Invalid set ID. Please refresh the page and try again.');
      return;
    }

    // Debug logging - comprehensive
    console.log('🔄 [QuickReplySetItemsScreen] Reordering items:', {
      setId: actualSetId,
      setIdType: typeof actualSetId,
      setIdLength: actualSetId.length,
      itemIds,
      itemIdsCount: itemIds.length,
      itemIdsTypes: itemIds.map(id => typeof id),
      items: newItems.map((item, idx) => ({ 
        index: idx, 
        _id: item._id, 
        _idType: typeof item._id,
        _idString: item._id ? String(item._id) : 'MISSING',
        displayText: item.displayText,
        displayOrder: item.displayOrder
      }))
    });

    try {
      const response = await apiService.reorderQuickReplyItems(actualSetId, itemIds);

      if (response.statusCode !== 1000) {
        // Revert on error
        setItems(originalItems);
        const errorMessage = response.errors?.[0]?.message || 'Failed to reorder items';
        console.error('❌ [QuickReplySetItemsScreen] Reorder failed:', {
          statusCode: response.statusCode,
          errorMessage,
          itemIds,
          response
        });
        showNotification('error', 'Error', errorMessage);
      } else {
        // Success - items are already in the correct order from optimistic update
        console.log('✅ [QuickReplySetItemsScreen] Reorder successful');
        // No need to reload - UI is already updated optimistically
      }
    } catch (error: any) {
      // Revert on error
      setItems(originalItems);
      console.error('❌ [QuickReplySetItemsScreen] Error reordering items:', error);
      showNotification('error', 'Error', error.message || 'Failed to reorder items');
      // Don't auto-reload - let user manually refresh if needed
    }
  };

  const handleSaveButtonText = async () => {
    if (items.length <= 3) {
      // Button text is only required for 4+ items
      return;
    }

    if (!listButtonText.trim()) {
      showNotification('error', 'Error', 'Button text is required when there are 4 or more items');
      return;
    }

    try {
      setSaving(true);
      const response = await apiService.updateQuickReplySet(actualSetId!, {
        listButtonText: listButtonText.trim(),
      });

      if (response.statusCode === 1000) {
        showNotification('success', 'Success', 'Button text saved successfully');
      } else {
        const errorMessage = response.errors?.[0]?.message || 'Failed to save button text';
        showNotification('error', 'Error', errorMessage);
      }
    } catch (error: any) {
      console.error('Error saving button text:', error);
      showNotification('error', 'Error', error.message || 'Failed to save button text');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (Platform.OS === 'web') {
      window.history.back();
    } else {
      navigateTo('quick-replies');
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const getItemTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      fixed_text: 'Fixed Text',
      data_set: 'Nested Set',
      product: 'Product',
      catalog: 'Catalog',
      webhook: 'Webhook',
      url: 'URL',
      task: 'Task',
      calendar: 'Calendar',
      assign_user: 'Assign User',
      assign_team: 'Assign Team',
    };
    return labels[type] || type;
  };

  const renderItem = ({ item, index }: { item: any; index: number }) => (
    <View style={styles.itemCard}>
      <View style={styles.itemHeader}>
        <View style={styles.itemInfo}>
          <View style={styles.itemTitleRow}>
            {item.icon && <Text style={styles.itemIcon}>{item.icon}</Text>}
            <Text style={styles.itemDisplayText}>{item.displayText}</Text>
            {!item.isActive && (
              <View style={styles.inactiveBadge}>
                <Text style={styles.inactiveBadgeText}>Inactive</Text>
              </View>
            )}
          </View>
          <Text style={styles.itemType}>{getItemTypeLabel(item.type)}</Text>
        </View>
        <View style={styles.itemActions}>
          <TouchableOpacity
            style={[styles.actionButton, index === 0 && styles.actionButtonDisabled]}
            onPress={() => handleMoveItem(item._id, 'up')}
            disabled={index === 0}
            activeOpacity={0.7}
          >
            <Ionicons
              name="chevron-up"
              size={20}
              color={index === 0 ? colors.textLight : colors.primary}
            />
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.actionButton, index === items.length - 1 && styles.actionButtonDisabled]}
            onPress={() => handleMoveItem(item._id, 'down')}
            disabled={index === items.length - 1}
            activeOpacity={0.7}
          >
            <Ionicons
              name="chevron-down"
              size={20}
              color={index === items.length - 1 ? colors.textLight : colors.primary}
            />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => handleDeleteItem(item._id)}
            activeOpacity={0.7}
          >
            <Ionicons name="trash-outline" size={20} color={colors.error} />
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );

  if (loading) {
    return (
      <ScreenLayout
        header={
          <Header
            showNotifications={true}
            showMessages={true}
            onNotificationPress={() => navigateTo('notifications')}
            onMessagePress={() => navigateTo('chat')}
            showNavigation={Platform.OS === 'web'}
            activeTab={activeTab}
            onTabPress={handleTabPress}
          />
        }
        showBottomTabs={Platform.OS !== 'web'}
        formHeader={<FormHeader title="Manage Items" onBack={handleBack} />}
      >
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Loading items...</Text>
        </View>
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      rightPaneContent="empty"
      formHeader={
        <FormHeader
          title={quickReplySet?.name || 'Reply Set'}
          subtitle={quickReplySet?.description}
          onBack={handleBack}
          actions={[{ icon: 'add', primary: true, onPress: async () => { await loadAvailableItems(); setShowPickItemsModal(true); }, accessibilityLabel: 'Add item' }]}
        />
      }
    >
      <View style={styles.container}>
        {/* Set Info (stats/badge/button-text) and the "Items" section title
            now scroll away WITH the list instead of sitting permanently
            fixed below FormHeader — rendered via FlatList's own
            ListHeaderComponent, the idiomatic RN way to put header-ish
            content above a list while keeping it part of the same
            scrollable region, rather than a second always-visible bar. */}
        <FlatList
          style={styles.itemsSection}
          data={items}
          renderItem={renderItem}
          keyExtractor={(item, index) => item._id || `item-${index}`}
          contentContainerStyle={styles.listContent}
          showsVerticalScrollIndicator={false}
          ListHeaderComponent={
            <>
              <View style={styles.setInfoSection}>
                {quickReplySet?.isDefault && (
                  <View style={styles.defaultBadge}>
                    <Ionicons name="star" size={16} color={colors.warning} />
                    <Text style={styles.defaultBadgeText}>Default</Text>
                  </View>
                )}
                <View style={styles.setStats}>
                  <View style={styles.statItem}>
                    <Ionicons name="list-outline" size={20} color={colors.primary} />
                    <Text style={styles.statValue}>{items.length}</Text>
                    <Text style={styles.statLabel}>{items.length === 1 ? 'Item' : 'Items'}</Text>
                  </View>
                  {quickReplySet?.metadata?.usageCount !== undefined && (
                    <View style={styles.statItem}>
                      <Ionicons name="stats-chart-outline" size={20} color={colors.success} />
                      <Text style={styles.statValue}>{quickReplySet.metadata.usageCount}</Text>
                      <Text style={styles.statLabel}>Uses</Text>
                    </View>
                  )}
                </View>

                {/* List Button Text Input - shown when items > 3 */}
                {items.length > 3 && (
                  <View style={styles.buttonTextSection}>
                    <Text style={styles.buttonTextLabel}>List Button Text *</Text>
                    <TextInput
                      style={styles.buttonTextInput}
                      placeholder="e.g., Select Option, Choose Item"
                      value={listButtonText}
                      onChangeText={setListButtonText}
                      placeholderTextColor={colors.textLight}
                      editable={!saving}
                    />
                    <Text style={styles.buttonTextHint}>
                      Custom button label for WhatsApp list format when there are 4 or more items
                    </Text>
                    {!listButtonText.trim() && (
                      <View style={styles.warningBox}>
                        <Ionicons name="warning" size={16} color={colors.warning} />
                        <Text style={styles.warningText}>Button text is required for this set to work properly</Text>
                      </View>
                    )}
                    <TouchableOpacity
                      style={[styles.saveButtonTextButton, !listButtonText.trim() && styles.saveButtonTextButtonDisabled]}
                      onPress={handleSaveButtonText}
                      disabled={saving || !listButtonText.trim()}
                      activeOpacity={0.8}
                    >
                      {saving ? (
                        <ActivityIndicator size="small" color={colors.white} />
                      ) : (
                        <>
                          <Ionicons name="checkmark" size={18} color={colors.white} />
                          <Text style={styles.saveButtonTextButtonText}>Save Button Text</Text>
                        </>
                      )}
                    </TouchableOpacity>
                  </View>
                )}
              </View>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitle}>Items</Text>
              </View>
            </>
          }
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Ionicons name="list-outline" size={64} color={colors.textLight} />
              <Text style={styles.emptyText}>No items yet</Text>
              <Text style={styles.emptySubtext}>Add your first quick reply item to get started</Text>
              <TouchableOpacity
                style={styles.emptyAddButton}
                onPress={async () => {
                  await loadAvailableItems();
                  setShowPickItemsModal(true);
                }}
                activeOpacity={0.7}
              >
                <Ionicons name="list" size={20} color={colors.primary} />
                <Text style={styles.emptyAddButtonText}>Pick Existing Item</Text>
              </TouchableOpacity>
            </View>
          }
        />

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          visible={showDeleteModal}
          title="Delete Item"
          message="Are you sure you want to delete this item? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={confirmDeleteItem}
          onClose={() => {
            setShowDeleteModal(false);
            setDeletingItemId(null);
          }}
          type="error"
          loading={deleting}
        />

        {/* Pick Items Modal */}
        <Modal
          visible={showPickItemsModal}
          animationType="slide"
          presentationStyle="pageSheet"
          transparent={Platform.OS === 'web'}
          onRequestClose={() => {
            setShowPickItemsModal(false);
            setSelectedItemIds([]);
            setItemsSearchQuery('');
          }}
        >
          {Platform.OS === 'web' ? (
            <View style={styles.webOverlay}>
              <TouchableOpacity
                style={styles.webBackdrop}
                activeOpacity={1}
                onPress={() => {
                  setShowPickItemsModal(false);
                  setSelectedItemIds([]);
                  setItemsSearchQuery('');
                }}
              />
              <View style={styles.containerConstrained}>
                {/* Header */}
                <View style={styles.modalHeader}>
                  <View style={styles.headerLeft}>
                    <TouchableOpacity
                      onPress={() => {
                        setShowPickItemsModal(false);
                        setSelectedItemIds([]);
                        setItemsSearchQuery('');
                      }}
                      style={styles.closeButton}
                    >
                      <Ionicons name="close" size={24} color={colors.textDark} />
                    </TouchableOpacity>
                    <Text style={styles.modalTitle}>Select Items</Text>
                  </View>
                  {selectedItemIds.length > 0 && (
                    <Text style={styles.selectedCount}>
                      {selectedItemIds.length} {selectedItemIds.length === 1 ? 'item' : 'items'} selected
                    </Text>
                  )}
                </View>

                {/* Search Bar */}
                <View style={styles.searchContainer}>
                  <View style={styles.searchInputContainer}>
                    <Ionicons name="search" size={20} color={colors.textLight} style={styles.searchIcon} />
                    <Input
                      placeholder="Search items..."
                      value={itemsSearchQuery}
                      onChangeText={setItemsSearchQuery}
                      style={styles.searchInput}
                    />
                  </View>
                </View>

                {/* Items List */}
                {loadingItems ? (
                  <View style={styles.centerContainer}>
                    <ActivityIndicator size="large" color={colors.primary} />
                    <Text style={styles.loadingText}>Loading items...</Text>
                  </View>
                ) : availableItems.filter((item: any) =>
                  !itemsSearchQuery || 
                  item.displayText?.toLowerCase().includes(itemsSearchQuery.toLowerCase())
                ).length === 0 ? (
                  <View style={styles.centerContainer}>
                    <Ionicons name="list-outline" size={64} color={colors.textLight} />
                    <Text style={styles.emptyText}>No available items</Text>
                    <Text style={styles.emptySubtext}>
                      {itemsSearchQuery ? 'Try a different search term' : 'All items are already in this set'}
                    </Text>
                  </View>
                ) : (
                  <FlatList
                    data={availableItems.filter((item: any) =>
                      !itemsSearchQuery || 
                      item.displayText?.toLowerCase().includes(itemsSearchQuery.toLowerCase())
                    )}
                    renderItem={({ item }) => {
                      const isSelected = selectedItemIds.includes(item._id);
                      return (
                        <TouchableOpacity
                          style={[styles.itemOption, isSelected && styles.itemOptionSelected]}
                          onPress={() => handleItemToggle(item._id)}
                          activeOpacity={0.7}
                        >
                          <View style={styles.itemCheckbox}>
                            {isSelected ? (
                              <Ionicons name="checkmark-circle" size={24} color={colors.primary} />
                            ) : (
                              <Ionicons name="ellipse-outline" size={24} color={colors.border} />
                            )}
                          </View>
                          <View style={styles.itemOptionContent}>
                            {item.icon && <Text style={styles.itemOptionIcon}>{item.icon}</Text>}
                            <View style={styles.itemOptionTextContainer}>
                              <Text style={styles.itemOptionText}>{item.displayText}</Text>
                              <Text style={styles.itemOptionType}>{getItemTypeLabel(item.type)}</Text>
                            </View>
                          </View>
                        </TouchableOpacity>
                      );
                    }}
                    keyExtractor={(item) => item._id}
                    contentContainerStyle={styles.listContent}
                  />
                )}

                {/* Footer Actions */}
                <View style={styles.footerActions}>
                  <Button
                    title="Cancel"
                    onPress={() => {
                      setShowPickItemsModal(false);
                      setSelectedItemIds([]);
                      setItemsSearchQuery('');
                    }}
                    variant="outline"
                    style={styles.cancelButton}
                  />
                  <Button
                    title="Add Selected"
                    onPress={handleAddExistingItems}
                    variant="primary"
                    disabled={selectedItemIds.length === 0 || saving}
                    loading={saving}
                    style={styles.confirmButton}
                  />
                </View>
              </View>
            </View>
          ) : (
            <View style={styles.container}>
              {/* Header */}
              <View style={styles.modalHeader}>
                <View style={styles.headerLeft}>
                  <TouchableOpacity
                    onPress={() => {
                      setShowPickItemsModal(false);
                      setSelectedItemIds([]);
                      setItemsSearchQuery('');
                    }}
                    style={styles.closeButton}
                  >
                    <Ionicons name="close" size={24} color={colors.textDark} />
                  </TouchableOpacity>
                  <Text style={styles.modalTitle}>Select Items</Text>
                </View>
                {selectedItemIds.length > 0 && (
                  <Text style={styles.selectedCount}>
                    {selectedItemIds.length} {selectedItemIds.length === 1 ? 'item' : 'items'} selected
                  </Text>
                )}
              </View>

              {/* Search Bar */}
              <View style={styles.searchContainer}>
                <View style={styles.searchInputContainer}>
                  <Ionicons name="search" size={20} color={colors.textLight} style={styles.searchIcon} />
                  <Input
                    placeholder="Search items..."
                    value={itemsSearchQuery}
                    onChangeText={setItemsSearchQuery}
                    style={styles.searchInput}
                  />
                </View>
              </View>

              {/* Items List */}
              {loadingItems ? (
                <View style={styles.centerContainer}>
                  <ActivityIndicator size="large" color={colors.primary} />
                  <Text style={styles.loadingText}>Loading items...</Text>
                </View>
              ) : availableItems.filter((item: any) =>
                !itemsSearchQuery || 
                item.displayText?.toLowerCase().includes(itemsSearchQuery.toLowerCase())
              ).length === 0 ? (
                <View style={styles.centerContainer}>
                  <Ionicons name="list-outline" size={64} color={colors.textLight} />
                  <Text style={styles.emptyText}>No available items</Text>
                  <Text style={styles.emptySubtext}>
                    {itemsSearchQuery ? 'Try a different search term' : 'All items are already in this set'}
                  </Text>
                </View>
              ) : (
                <FlatList
                  data={availableItems.filter((item: any) =>
                    !itemsSearchQuery || 
                    item.displayText?.toLowerCase().includes(itemsSearchQuery.toLowerCase())
                  )}
                  renderItem={({ item }) => {
                    const isSelected = selectedItemIds.includes(item._id);
                    return (
                      <TouchableOpacity
                        style={[styles.itemOption, isSelected && styles.itemOptionSelected]}
                        onPress={() => handleItemToggle(item._id)}
                        activeOpacity={0.7}
                      >
                        <View style={styles.itemCheckbox}>
                          {isSelected ? (
                            <Ionicons name="checkmark-circle" size={24} color={colors.primary} />
                          ) : (
                            <Ionicons name="ellipse-outline" size={24} color={colors.border} />
                          )}
                        </View>
                        <View style={styles.itemOptionContent}>
                          {item.icon && <Text style={styles.itemOptionIcon}>{item.icon}</Text>}
                          <View style={styles.itemOptionTextContainer}>
                            <Text style={styles.itemOptionText}>{item.displayText}</Text>
                            <Text style={styles.itemOptionType}>{getItemTypeLabel(item.type)}</Text>
                          </View>
                        </View>
                      </TouchableOpacity>
                    );
                  }}
                  keyExtractor={(item) => item._id}
                  contentContainerStyle={styles.listContent}
                />
              )}

              {/* Footer Actions */}
              <View style={styles.footerActions}>
                <Button
                  title="Cancel"
                  onPress={() => {
                    setShowPickItemsModal(false);
                    setSelectedItemIds([]);
                    setItemsSearchQuery('');
                  }}
                  variant="outline"
                  style={styles.cancelButton}
                />
                <Button
                  title="Add Selected"
                  onPress={handleAddExistingItems}
                  variant="primary"
                  disabled={selectedItemIds.length === 0 || saving}
                  loading={saving}
                  style={styles.confirmButton}
                />
              </View>
            </View>
          )}
        </Modal>
      </View>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.lightGray,
  },
  setInfoSection: {
    backgroundColor: colors.background.surface,
    padding: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  defaultBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    backgroundColor: (colors.warning as string) + '20',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs / 2,
    borderRadius: designTokens.borderRadius.md,
    gap: spacing.xs / 2,
    marginBottom: spacing.sm,
  },
  defaultBadgeText: {
    fontSize: typography.fontSize.xs,
    color: colors.warning,
    fontFamily: typography.fontFamily.semibold,
    fontWeight: '600',
  },
  setStats: {
    flexDirection: 'row',
    gap: spacing.lg,
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  statValue: {
    fontSize: typography.fontSize.lg,
    fontWeight: '700',
    color: colors.textDark,
    fontFamily: typography.fontFamily.bold,
  },
  statLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  buttonTextSection: {
    marginTop: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.divider,
    gap: spacing.sm,
  },
  buttonTextLabel: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  buttonTextInput: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.primary,
    backgroundColor: colors.background.surfaceRaised,
  },
  buttonTextHint: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    fontStyle: 'italic',
  },
  warningBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    backgroundColor: (colors.warning as string) + '20',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: (colors.warning as string) + '60',
  },
  warningText: {
    fontSize: typography.fontSize.sm,
    color: colors.warning,
    fontFamily: typography.fontFamily.primary,
    flex: 1,
  },
  saveButtonTextButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: designTokens.borderRadius.md,
    marginTop: spacing.xs,
  },
  saveButtonTextButtonDisabled: {
    opacity: 0.5,
  },
  saveButtonTextButtonText: {
    color: colors.white,
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    fontFamily: typography.fontFamily.semibold,
  },
  itemsSection: {
    flex: 1,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: '700',
    color: colors.textDark,
    fontFamily: typography.fontFamily.bold,
  },
  listContent: {
    padding: spacing.md,
    paddingBottom: spacing.xl,
  },
  itemCard: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    padding: spacing.md,
    marginBottom: spacing.md,
    ...designTokens.shadows.sm,
  },
  itemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  itemInfo: {
    flex: 1,
    marginRight: spacing.md,
  },
  itemTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.xs / 2,
  },
  itemIcon: {
    fontSize: 20,
  },
  itemDisplayText: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    flex: 1,
  },
  inactiveBadge: {
    backgroundColor: (colors.warning as string) + '20',
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderRadius: designTokens.borderRadius.sm,
  },
  inactiveBadgeText: {
    fontSize: typography.fontSize.xs,
    color: colors.warning,
    fontFamily: typography.fontFamily.medium,
  },
  itemType: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  itemActions: {
    flexDirection: 'row',
    gap: spacing.xs,
  },
  actionButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.lightGray,
    justifyContent: 'center',
    alignItems: 'center',
  },
  actionButtonDisabled: {
    opacity: 0.3,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl * 2,
  },
  emptyText: {
    fontSize: typography.fontSize.xl,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    marginTop: spacing.lg,
    marginBottom: spacing.xs,
  },
  emptySubtext: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    textAlign: 'center',
    marginBottom: spacing.xl,
  },
  emptyAddButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primary + '15',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: designTokens.borderRadius.md,
    gap: spacing.xs,
  },
  emptyAddButtonText: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: colors.background.base,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border + '40',
    ...(Platform.OS === 'web' && {
      position: 'sticky' as any,
      top: 0,
      backgroundColor: colors.background.surface,
      zIndex: 10,
    }),
  },
  modalTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  closeButton: {
    padding: spacing.xs,
    marginRight: spacing.sm,
  },
  selectedCount: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  webOverlay: {
    flex: 1,
    position: 'relative' as any,
  },
  webBackdrop: {
    position: 'absolute' as any,
    top: 180, // Header height
    left: 355, // Left pane width
    right: 355, // Right pane width
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1,
  },
  containerConstrained: {
    position: 'absolute' as any,
    top: 180, // Header height (matches webContentArea paddingTop)
    left: 355, // Left pane width
    right: 355, // Right pane width
    bottom: 0,
    backgroundColor: colors.background.surface,
    zIndex: 2,
    borderRadius: designTokens.borderRadius.lg,
    margin: spacing.lg,
    overflow: 'hidden',
    ...designTokens.shadows.lg,
  },
  addOptionsContainer: {
    backgroundColor: colors.background.surfaceRaised,
    borderRadius: designTokens.borderRadius.lg,
    padding: spacing.md,
    minWidth: 250,
    ...designTokens.shadows.lg,
  },
  addOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    borderRadius: designTokens.borderRadius.md,
    gap: spacing.md,
  },
  addOptionText: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  searchContainer: {
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border + '40',
  },
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    position: 'relative',
  },
  searchIcon: {
    position: 'absolute',
    left: spacing.sm,
    zIndex: 1,
  },
  searchInput: {
    paddingLeft: spacing.xl + spacing.sm,
  },
  listContent: {
    padding: spacing.md,
  },
  itemOption: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.md,
    padding: spacing.sm,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border + '40',
  },
  itemOptionSelected: {
    borderColor: colors.primary,
    backgroundColor: colors.primary + '10',
  },
  itemCheckbox: {
    marginRight: spacing.sm,
  },
  itemOptionContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  itemOptionIcon: {
    fontSize: 24,
  },
  itemOptionTextContainer: {
    flex: 1,
  },
  itemOptionText: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    marginBottom: spacing.xs / 2,
  },
  itemOptionType: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  emptyText: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    marginTop: spacing.md,
  },
  emptySubtext: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    marginTop: spacing.xs,
  },
  footerActions: {
    flexDirection: 'row',
    gap: spacing.md,
    padding: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.border + '40',
    backgroundColor: colors.background.surface,
    ...(Platform.OS === 'web' && {
      position: 'sticky' as any,
      bottom: 0,
    }),
  },
  cancelButton: {
    flex: 1,
  },
  confirmButton: {
    flex: 1,
  },
});

